import React, { useEffect, useRef, useState } from 'react'
import { Alert, Form } from 'antd'
import { Formik } from 'formik'
import { object, setLocale, string } from 'yup'
import { useDispatch, useSelector } from 'react-redux'

import { ApplicationState } from 'src/store'
import { Input, InputPassword } from 'src/components/Formik'
import { Modal } from 'src/components/Ant'
import { setIntegrationCredential } from 'src/store/ducks/integrations/actions'

interface Props {
  toggle: () => void
  username: string
  visible: boolean
}

setLocale({
  mixed: {
    required: 'common.required'
  }
})

const validationSchema = object().shape({
  password: string()
    .matches(/^[^\s]+$/, {
      message: 'web.side.user.change-password.password.invalid-character'
    })
    .min(6, 'web.side.user.change-password.password.min-length')
    .required(),
  username: string().required()
})

function ModalPasswordConfirmation({
  toggle,
  username,
  visible
}: Props): JSX.Element {
  const {
    integrations: { errorRenew, loading }
  } = useSelector((state: ApplicationState) => ({
    integrations: state.integrations
  }))

  const dispatch = useDispatch()
  const refFormik = useRef(null)
  const [onSubmit, setOnSubmit] = useState<boolean>(false)
  const initialValues = { username, password: '' }

  function handleSubmit(values: typeof initialValues) {
    dispatch(setIntegrationCredential(values))
    setOnSubmit(true)
  }

  useEffect(() => {
    if (onSubmit && !errorRenew && !loading) {
      setOnSubmit(false)
      toggle()
    }
  }, [onSubmit, errorRenew, loading, toggle])

  return (
    <Modal
      destroyOnClose
      okButtonProps={{ loading }}
      okText="common.actions.send"
      onCancel={toggle}
      onOk={() => {
        refFormik.current.submitForm()
      }}
      title="web.modal-password-confirmation.title"
      visible={visible}
      width={400}
    >
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        ref={refFormik}
        render={formikProps => (
          <Form
            onSubmit={formikProps.handleSubmit}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                formikProps.handleSubmit()
              }
            }}
          >
            {!!errorRenew && (
              <Form.Item>
                <Alert message={errorRenew} showIcon type="error" />
              </Form.Item>
            )}
            <Input
              disabled
              formikProps={formikProps}
              label="web.integration.modal-password-confirmation.username"
              name="username"
              placeholder="web.side.user.change-password.password.placeholder"
            />
            <InputPassword
              formikProps={formikProps}
              label="web.login.password.title"
              name="password"
              placeholder="web.login.password.placeholder"
            />
          </Form>
        )}
        validationSchema={validationSchema}
      />
    </Modal>
  )
}

export default ModalPasswordConfirmation
